import { CommonModule, ViewportScroller } from '@angular/common';
import { AfterViewInit, Component, Input, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, NgModel, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AccountService } from '../../../services/account.service';
import { ProfileService } from '../../../services/profile.service';
import { ACCOUNT_URLS } from '../../app.routes.names';
import { AppState } from '../../app.service';
import { PhoneMaskDirective } from '../../directives/phone-mask.directive';
import { RestrictNumbersDirective } from '../../directives/restrict-numbers.directive';
import { ShopifyBuyService } from '../../../services/shopify.service';
import { ToastHelperService } from '../../../services/toast-helper.service';
import { TopupService } from '../../../services/topup.service';
import { IUserMdn } from '../../../interfaces/account';

@Component({
  selector: 'app-top-up-card',
  standalone: true,
  imports: [CommonModule, TranslateModule, ReactiveFormsModule, RestrictNumbersDirective, PhoneMaskDirective, FormsModule],
  templateUrl: './top-up-card.component.html',
  styleUrl: './top-up-card.component.scss'
})
export class TopUpCardComponent implements OnInit, AfterViewInit {
  @Input() planId: string = '';
  @Input() noteDesc: string = '';
  @Input() specificTitle: string = '';
  @Input() verificationRequired: boolean;
  @Input() redeemVoucherFlow: boolean;
  @ViewChild('balanceModel') balanceModel!: NgModel;
  public showTopupSection = false;
  public quickTopUpForm: FormGroup;
  public voucherForm: FormGroup;
  public selectMdnForm: FormGroup;
  public showMdnStep = false;
  public showVoucherStep = false;
  public vouchers: any[] = [];
  public selectedVoucher: any = null;
  public mdn: string = '';
  public valueChecked = true;
  public decimalPattern = new RegExp('^[0-9]+(\.[0-9]{1,2})?$');
  public balance;
  public renewAmount = 0;
  public maxRefillAmount = 2.5;
  public canTopup = true;
  public accountBalance = 0;
  numbers: IUserMdn[] = [];
  constructor(
    private shopifyBuyService: ShopifyBuyService,
    private appState: AppState,
    private formBuilder: FormBuilder,
    private toastHelper: ToastHelperService,
    private accountService: AccountService,
    private router: Router,
    private route: ActivatedRoute,
    private scroller: ViewportScroller,
    private toastService: ToastHelperService,
    private profileService: ProfileService,
    private topupService: TopupService
  ) { }

  ngOnInit(): void {
    this.quickTopUpForm = this.formBuilder.group({
      phoneNumber: ['', [Validators.required, Validators.maxLength(14)]],
      confirmPhoneNumber: ['', [Validators.required, Validators.maxLength(14)]],
    }, {
      validators: [this.matchingPhoneNumber('phoneNumber', 'confirmPhoneNumber'), this.matchingDigits('phoneNumber')]
    });

    this.voucherForm = this.formBuilder.group({
      voucherNumber: ['', Validators.compose([Validators.required, Validators.maxLength(16), Validators.minLength(12)])],
      confirmVoucherNumber: ['', Validators.compose([Validators.required, Validators.maxLength(16), Validators.minLength(12)])],
    }, { validator: this.matchingVoucherNumber('voucherNumber', 'confirmVoucherNumber') });
    this.prefillTopupFromNavigation();
    this.selectMdnForm = this.formBuilder.group({
      phoneNumber: ['', [Validators.required, Validators.maxLength(14)]],
    });
  }

  ngAfterViewInit(): void {
    this.route.fragment.subscribe((fragment) => {
      if (fragment === 'topup') {
        this.openTopUpSection();
        setTimeout(() => {
          this.scroller.setOffset([0, 100]);
          this.scroller.scrollToAnchor('topup');
        }, 0);
      }
    });
  }

  openTopUpSection(): void {
    this.showTopupSection = true;
    this.showMdnStep = true;
    this.showVoucherStep = false;
    if (!this.redeemVoucherFlow) {
      this.getMdns();
    }
  }

  selectVoucher(voucher: any): void {
    this.selectedVoucher = voucher;
    this.valueChecked = true;
  }

  goToShopify(): void {
    this.balanceModel.control.markAsTouched();
    this.valueChecked = !!this.balance && this.balance > 0 && this.balance <= this.maxRefillAmount ? true : false;
    if (!this.valueChecked) {
      return;
    }
    this.appState.loading = true;
    this.shopifyBuyService.createDynamicPayment(this.balance, this.mdn).then((data) => {
      window.location.href = data;
    }, (error) => {
      this.appState.loading = false;
      this.toastHelper.error(error.message);
    });
  }

  reset(): void {
    this.showTopupSection = false;
    this.showMdnStep = false;
    this.showVoucherStep = false;
    this.mdn = '';
    this.selectedVoucher = null;
    this.quickTopUpForm.reset();
    this.selectMdnForm.reset();
    this.balance = null;
    this.canTopup = true;
    this.maxRefillAmount = 2.5;
    this.accountBalance = 0;
  }
  submitTopup(): void {
     this.voucherForm.markAllAsTouched();
    if (!this.voucherForm.valid) {
      return;
    } else {
      const code = this.voucherForm.controls['voucherNumber'].value;
      this.appState.loading = true;
      this.topupService.redeemVoucher(this.mdn, code).then(() => {
        this.appState.loading = false;
        this.toastHelper.success("Account Balance updated successfully!");
        this.router.navigate([`${ACCOUNT_URLS.BASE}/${ACCOUNT_URLS.SUMMARY}`], {
          queryParams: this.mdn ? { mdn: this.mdn } : {}
        });
        }, (error) => {
          this.appState.loading = false;
          this.toastHelper.error(error.error.errors[0].message);
        });
    }
   
  }
  onPaste(event: Event): void {
    event.preventDefault();
  }

  goToVoucher(): void {
      this.quickTopUpForm.markAllAsTouched();
      if (!this.quickTopUpForm.valid) {
        return;
      }
    this.continueToPhoneValidation();
   
  }
  next(): void {
      this.selectMdnForm.markAllAsTouched();
      if (!this.selectMdnForm.valid) {
        return;
      }
    this.continueToPhoneValidation(true);
   
  }

  private continueToPhoneValidation(select?): void {
    this.appState.loading = true;
    if (select) {
      this.mdn = this.selectMdnForm.controls['phoneNumber'].value.replace(/\D/g, '');
    } else {
      this.mdn = this.quickTopUpForm.controls['phoneNumber'].value.replace(/\D/g, '');
    }
    this.accountService.validatePhoneNumber(this.mdn).then((data) => {
      this.appState.loading = false;
      this.showMdnStep = false;
      this.renewAmount = !!data?.renewal_cost ? data?.renewal_cost : 0;
      this.accountBalance = !!data?.account_balance ? data?.account_balance : 0;
      this.maxRefillAmount = this.renewAmount - this.accountBalance > 2.5 ? this.renewAmount - this.accountBalance : 2.5;
      this.balance = this.maxRefillAmount;
      this.canTopup = this.renewAmount > this.accountBalance;
      this.showVoucherStep = this.redeemVoucherFlow ? true : this.canTopup;
    }, error => {
      this.appState.loading = false;
      this.toastHelper.error(error?.error?.errors?.[0]?.message);
    });
  }

  private matchingPhoneNumber(mdn: string, confirmMdn: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control instanceof FormGroup)) {
        return null;
      }

      const phoneNumber = control.get(mdn);
      const confirmPhoneNumber = control.get(confirmMdn);

      if (
        phoneNumber?.value &&
        confirmPhoneNumber?.value &&
        phoneNumber.value !== confirmPhoneNumber.value
      ) {
        return { mismatchedPhoneNumber: true };
      }

      return null;
    };
  }

  private matchingDigits(mdn: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!(control instanceof FormGroup)) {
        return null;
      }

      const phoneNumber = control.get(mdn);
      if (!phoneNumber?.value) {
        return null;
      }

      const isMatching = /^\D*(\d)(?:\D*|\1)*$/.test(phoneNumber.value);
      return isMatching ? { matchingDigits: true } : null;
    };
  }

  private prefillTopupFromNavigation(): void {
    const navigationState = this.router.getCurrentNavigation()?.extras?.state ?? history.state;
    const prefillMdn = navigationState?.prefillMdn;

    if (!prefillMdn) {
      return;
    }

    const digitsOnly = prefillMdn.toString().replace(/\D/g, '').slice(0, 10);
    const formattedMdn = this.formatMdn(digitsOnly);

    this.openTopUpSection();
    this.mdn = digitsOnly;
    this.quickTopUpForm.patchValue({
      phoneNumber: formattedMdn,
      confirmPhoneNumber: formattedMdn
    });
  }

  private formatMdn(mdn: string): string {
    if (!mdn) {
      return '';
    }

    const area = mdn.slice(0, 3);
    const prefix = mdn.slice(3, 6);
    const line = mdn.slice(6, 10);

    if (mdn.length <= 3) {
      return `(${area}`;
    }
    if (mdn.length <= 6) {
      return `(${area}) ${prefix}`;
    }
    return `(${area}) ${prefix}-${line}`;
  }
private matchingVoucherNumber(pin: string, confirmPin: string): any {
  return (group: FormGroup): ValidationErrors | null => {
    const voucherNumber = group.get(pin);
    const confirmVoucherNumber = group.get(confirmPin);

    if (voucherNumber && confirmVoucherNumber && !!voucherNumber?.value && !!confirmVoucherNumber?.value && voucherNumber.value !== confirmVoucherNumber.value) {
      confirmVoucherNumber.setErrors({ mismatchedVoucherNumber: true });
      return { mismatchedVoucherNumber: true };
    }

    if (confirmVoucherNumber && confirmVoucherNumber.hasError('mismatchedVoucherNumber')) {
      const errors = { ...confirmVoucherNumber.errors };
      delete errors['mismatchedVoucherNumber']; 
      confirmVoucherNumber.setErrors(Object.keys(errors).length ? errors : null);
    }

    return null; 
  };
}
  private getMdns(): void {
    this.appState.loading = true;
    this.accountService.getUserMdns().then((result) => {
      this.appState.loading = false;
      this.numbers = result.mdns;
    }).catch((error) => {
      this.numbers = [];
      this.appState.loading = false;
      this.toastService.error(error?.error?.errors[0]?.message);
    });
  }

}
